/**
 * TopBar — Figma `Shell/TopBar` (node 12:982), 56px.
 *
 * Measured: `bg/surface` with a bottom `border/default`, px-24; the breadcrumb is
 * `Caption/12` `text/muted` + `Body/14-Med` `text/primary`; the actions group is a
 * 280×36 `bg/subtle` search at `radius/sm`, a 36px bell, a 24px `border/default`
 * hairline, and a "Log Out" in `Body/14-Med` `text/secondary`.
 *
 * ===========================================================================
 * TWO DESIGNED CONTROLS ARE RENDERED DISABLED RATHER THAN FAKED
 * ===========================================================================
 * · **Global search** ("Search customers, CRFs…"). There is no search endpoint in
 *   spec §10 — `?q=` exists per LIST, scoped to that list, and CRFs do not exist
 *   until Phase 3. A box that looks like global search and silently searches one
 *   list would be worse than one that says it is not ready.
 * The GLOBAL SEARCH box stays visible — deleting designed chrome is itself a
 * divergence — and carries a title explaining why. Recorded in PHASE2-REPORT.
 *
 * ===========================================================================
 * THE BELL IS NO LONGER ONE OF THEM (A3)
 * ===========================================================================
 * It shipped disabled with this reason: "`notifications` is a Phase 0 table with
 * no read route in this phase's contract, so the bell has nothing to open and no
 * count to show. Figma draws an unread dot; drawing one over zero data would be a
 * fabricated number."
 *
 * That was true and is now STALE. Phase 4 built `GET /me/notifications` and two
 * live wire-ups that write to it, so the dot is a real count from a real route.
 * A disabled bell here would now mean the app knows a developer has been told
 * something and declines to show them — which is the opposite failure to the one
 * the original decision was avoiding. See `features/notifications/`.
 *
 * The THEME TOGGLE is an addition, not a Figma element: the token collection ships
 * a Dark mode, and a mode with no way to reach it is a dead half of the design
 * system. It is placed in the actions group next to the other icon control.
 *
 * ===========================================================================
 * THE BAR RECEDES — the chrome seam, owner option B
 * ===========================================================================
 * The owner asked for the sidebar and the top bar to be told apart BY COLOUR.
 * They could not be. `--crf-bg-sidebar` and `--crf-bg-surface` are both #ffffff
 * in Light and both #1f1f1f in Dark (`styles/tokens.css`), so the seam between
 * the two pieces of chrome measured **1.00:1** with a 1px `border/default` in it
 * — the rail and the bar were one continuous plane.
 *
 * The fix moves this header's GROUND rather than any token's VALUE: the bar
 * takes the authored canvas (`bg-canvas`, #eef1f6 — D3 as amended by MC-12),
 * which the shell already paints under the content. Measured **1.13:1** against
 * the white rail: the same lift the content cards carry, deliberately below
 * every WCAG bar because it separates two SURFACES and carries no meaning. The
 * SIDEBAR IS NOT TOUCHED — its ground, its hairline in light, and its two open
 * AA findings are exactly where they were.
 *
 * In Dark `bg-canvas` resolves to the shipped `bg/page` #161616, so the bar
 * recedes there too, at 1.09:1. Dark's seam is carried by the RULE between the
 * two surfaces rather than by the fill (see `Sidebar.tsx` — the arithmetic
 * ceiling for any darker chrome against #1f1f1f is 1.27:1, so there is almost
 * nothing left to win with a fill).
 *
 * ===========================================================================
 * ⚠ HOUSE RULE 5 CALLOUT — MOVING THE GROUND MOVED TWO LABELS WITH IT
 * ===========================================================================
 * Both are marked at their call sites below. Neither is polish; each is the
 * measured price of the line above, paid in the same commit that incurs it.
 *
 *  1. **`Log Out`: `text-fg-secondary` → `text-fg`.** REGRESSED BY THIS CHANGE.
 *     #6e7580 measures 4.64:1 on the white bar (legal) and **4.10:1** on the
 *     canvas — under AA, and the exact pair the mockups' §7 table marks
 *     FORBIDDEN on the bare canvas.
 *  2. **The breadcrumb TRAIL: `text-fg-muted` → `text-fg`.** ALREADY ILLEGAL
 *     before this change at 2.63:1 on white; the canvas takes it to **2.32:1**.
 *     `docs/WEB-DESIGN-DIRECTION.md` §6.4 rules that `--crf-text-muted` has no
 *     legal use in light mode at all.
 *
 * The named cost of (2) is that the breadcrumb's hierarchy collapses onto SIZE
 * and WEIGHT — trail and current crumb are now the same ink. That is D3's own
 * recorded consequence, arriving at the top bar. `top-bar-chrome.test.tsx`
 * asserts the separation that remains rather than trusting it.
 *
 * `lever7-contrast.test.ts` measures the seam and both refusals.
 */

import { Search } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CountryCode } from '../../domain/countries';
import { NotificationBell } from '../../features/notifications/NotificationBell';
import { CountrySwitcher } from './CountrySwitcher';
import { ThemeToggle } from './ThemeToggle';

export interface TopBarProps {
  /** e.g. ['Pipeline', 'Dashboard'] — the last item is the current page. */
  breadcrumb: readonly string[];
  countryCode: CountryCode;
  accessibleCountries: readonly CountryCode[];
  onSignOut: () => void;
  /** Page-specific actions (a "New inquiry" button, a view toggle). */
  actions?: ReactNode;
}

export function TopBar({
  breadcrumb,
  countryCode,
  accessibleCountries,
  onSignOut,
  actions,
}: TopBarProps): React.JSX.Element {
  const trail = breadcrumb.slice(0, -1);
  const current = breadcrumb[breadcrumb.length - 1] ?? '';

  return (
    <header className="flex h-topbar shrink-0 items-center justify-between gap-4 border-b border-line bg-canvas px-6">
      <div className="flex min-w-0 items-center gap-4">
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
          {trail.map((crumb) => (
            /* ⚠ RULE 5 (2) — `text-fg`, not `text-fg-muted`. #9aa0aa was already
             * illegal here at 2.63:1 on the white bar (§6.4: no legal use in
             * light mode) and the canvas takes it to 2.32:1. `text-fg-secondary`
             * is not the destination §6.4's sweep would give it, because on THIS
             * ground that pair is the 4.10:1 refusal above. The trail therefore
             * takes text/primary and keeps its hierarchy on SIZE (Caption/12 at
             * weight 400) against the current crumb's Body/14-Med. */
            <span key={crumb} className="shrink-0 text-label text-fg">
              {crumb} /
            </span>
          ))}
          <span className="truncate text-body font-medium text-fg">{current}</span>
        </nav>
        <div aria-hidden="true" className="h-6 w-px shrink-0 bg-line" />
        <CountrySwitcher current={countryCode} accessible={accessibleCountries} />
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {actions}
        <div
          title="Global search arrives with the CRF module — until then, use the search on each list."
          className="hidden h-9 w-[280px] items-center gap-2 rounded-sm bg-subtle px-2.5 text-body13 text-fg-muted lg:flex dark:ring-1 dark:ring-line"
          aria-disabled="true"
        >
          <Search aria-hidden="true" className="size-3.5 shrink-0" />
          Search customers, CRFs…
        </div>
        <NotificationBell />
        <ThemeToggle />
        <div aria-hidden="true" className="h-6 w-px bg-line" />
        {/* ⚠ RULE 5 (1) — `text-fg`, not `text-fg-secondary`. This label PASSED
         * at 4.64:1 on the white bar and FAILS at 4.10:1 on the canvas; it moves
         * because the ground moved, in the same commit that moved it. The
         * `hover:text-fg` that used to be the lift is now the rest state, so the
         * hover reduces to its `bg-subtle` wash. */}
        <button
          type="button"
          onClick={onSignOut}
          className="inline-flex h-9 items-center justify-center rounded-sm px-3 text-body font-medium text-fg hover:bg-subtle"
        >
          Log Out
        </button>
      </div>
    </header>
  );
}
